// @flow

import isObject from 'lodash/isObject';
import entries from 'lodash/entries';
import { from } from 'rxjs';

import config from '../config';

const { API_END_POINT } = config;

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE';
type RequestDefinition = {
  ...RequestOptions,
  timeout?: number
};

class Api {
  state = {};

  apiGet = (url: string, params: Object = {}, config: Object = {}, authorized: boolean = true) => {
    return this.request('GET', url, params, {}, config, authorized);
  };

  apiPost = (
    url: string,
    params: Object = {},
    body: Object = {},
    config: Object = {},
    authorized: boolean = true
  ) => {
    return this.request('POST', url, params, body, config, authorized);
  };

  apiPut = (
    url: string,
    params: Object = {},
    body: Object = {},
    config: Object = {},
    authorized: boolean = true
  ) => {
    return this.request('PUT', url, params, body, config, authorized);
  };

  apiDelete = (
    url: string,
    params: Object = {},
    config: Object = {},
    authorized: boolean = true
  ) => {
    return this.request('DELETE', url, params, {}, config, authorized);
  };

  apiSave = (url: string, params: Object = {}, body: Object = {}, authorized: boolean = true) => {
    return body.id
      ? this.apiPut(`${url}/${body.id}`, params, body, authorized)
      : this.apiPost(url, params, body, authorized);
  };

  request(
    method: HttpMethod | 'FORM',
    url: string,
    params: Object = {},
    body: Object = {},
    requestConfig: Object = {},
    authorized: boolean = true
  ) {
    const { authentication = {}, core = {} } = this.state;
    const { pin, origin, externalUserId } = authentication;
    const { guid } = core;
    const urlParams = this._formatParams(params);
    const targetUrl = `${API_END_POINT}${url}`;
    const targetEndpoint = urlParams ? `${targetUrl}?${urlParams}` : targetUrl;
    const methodRequest = method === 'FORM' ? 'POST' : method;

    const token = (() => {
      if (origin && externalUserId) {
        return `IntegrationKiosk ${externalUserId}@${guid}`;
      }

      return pin ? `Kiosk ${pin}@${guid}` : `Kiosk ${guid}`;
    })();

    const req: RequestDefinition = {
      headers: {
        ...(authorized ? { Authorization: token } : {}),
        'Access-Control-Allow-Origin': '*'
      },
      crossDomain: true,
      responseType: 'json',
      method: methodRequest
    };

    if (requestConfig.timeout) {
      req.timeout = requestConfig.timeout;
    }

    if (method === 'POST' || method === 'PUT') {
      (req: any).headers['Content-Type'] = 'application/json';
      req.body = JSON.stringify(body);
    }

    if (method === 'FORM') {
      (req: any).headers['Content-Type'] = 'multipart/form-data';
      req.body = body;
    }

    return (from(
      fetch(targetEndpoint, req)
        .then(response => {
          if (!response.ok) {
            if (response.status === 401) {
              throw Error('401');
            }
            return response.json().then(error => {
              throw Error(
                typeof error === 'object' && error !== null && error.message ? error.message : error
              );
            });
          }
          return response.json();
        })
        .then(response => ({ response }))
        .catch(e => {
          if (e.toString() === 'TypeError: Failed to fetch') {
            throw new Error('Your network is down. Sling will not function properly without it.');
          }
          throw e;
        })
    ): any); // @TODO: return observable type atleast
  }

  _formatParams(params: Object): string {
    return entries(params)
      .map(([key, val]) => {
        key = encodeURIComponent(key);
        if (Array.isArray(val)) {
          const encodedVals = val.map(encodeURIComponent);
          val = encodedVals.join(',');
        } else if (isObject(val)) {
          // @TODO: handle array / object serialization
          console.warn(`Unhandled request param value type Object for key ${key}`);
          return '';
        } else if (!this._isEncoded(val)) {
          val = encodeURIComponent(val);
        }
        return `${key}=${String(val)}`;
      })
      .join('&');
  }

  _isEncoded(uri: string): boolean {
    uri = uri || '';
    return uri !== decodeURIComponent(uri);
  }
}

export default new Api();
